import { useNavigate } from '@tanstack/react-router';
import { FlaskConical, ArrowRight, Trash2, Plus } from 'lucide-react';
import { useUebung, useUebungAnlegen, useUebungEntfernen } from '@/hooks/useUebung';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';

/**
 * „Ausprobieren" — ein Ort, an dem man etwas anfassen kann (A53, #142).
 *
 * WARUM DAS HIER STEHT UND NICHT IRGENDWO
 *   Der Betreiber konnte einen Prüfschritt nach dem Deploy nicht
 *   ausführen: „da man Bestellungen danach nicht löschen kann."
 *   Der naheliegende Rat — probier es auf der Entwicklungsinstanz —
 *   geht ins Leere. tracking-dev.buch-engel.com IST die Produktion; das
 *   „dev" im Namen stammt aus der Aufbauzeit. Eine getrennte
 *   Prüfinstanz gibt es nicht.
 *
 *   Damit ist die Übungsbestellung die einzige Möglichkeit, im
 *   laufenden System etwas auszuprobieren, ohne Müll zu hinterlassen.
 *   Sie gab es schon — nur keinen Weg zu ihr außerhalb der Tour.
 *
 * WARUM DIE ÜBERSCHRIFT „AUSPROBIEREN" HEISST
 *   Nicht „Übungsbestellung": Wer etwas testen will, sucht nach dem, was
 *   er VORHAT, nicht nach dem Mittel dazu — und dass es so ein Mittel
 *   überhaupt gibt, weiß er ja gerade nicht. Die Überschrift nennt den
 *   Zweck, die zwei Sätze darunter das Mittel.
 *
 * WAS ES HIER NICHT GIBT
 *   Keinen Weg, echte Bestellungen zu löschen. Der Wunsch ist
 *   „gefahrlos ausprobieren", nicht „löschen können" — das eine ist
 *   eine Spielwiese, das andere ein Loch im Boden.
 */
export function UebungAbschnitt({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useUebung();
  const anlegen = useUebungAnlegen();
  const entfernen = useUebungEntfernen();
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();
  const toast = useToast();

  const uebung = data?.bestellung ?? null;
  const laeuft = anlegen.isPending || entfernen.isPending;

  async function anlegenKlick(): Promise<void> {
    // Nur wenn es etwas zu verlieren gibt, wird gefragt. Eine Rückfrage
    // vor dem ersten Anlegen wäre eine Warnung ohne Gegenstand — und
    // wer sie zweimal weggeklickt hat, liest sie beim dritten Mal auch
    // dann nicht mehr, wenn sie zählt.
    if (uebung !== null) {
      const ok = await confirm({
        titel: 'Vorhandene Übungsbestellung ersetzen?',
        text:
          `${uebung.bestellnr} wird dabei gelöscht, mit allem, was du daran ` +
          'eingetragen hast. Eine neue Übung entsteht an ihrer Stelle.',
        bestaetigenLabel: 'Ersetzen',
        destruktiv: true
      });
      if (!ok) return;
    }
    try {
      const res = await anlegen.mutateAsync();
      toast('success', `Übungsbestellung ${res.bestellung.bestellnr} angelegt.`);
    } catch (err) {
      // Der Server schickt bei 409 einen fertigen deutschen Satz samt
      // Abhilfe („in den Einstellungen lässt sich ein stillgelegter
      // wieder einschalten"). Ihn zu zeigen ist besser als jeder eigene:
      // ein selbst erfundener Text kennt die Abhilfe nicht.
      const satz =
        err instanceof ApiError && err.code === 'kein_bestellweg'
          ? err.message
          : 'Die Übungsbestellung ließ sich nicht anlegen.';
      toast('error', satz);
    }
  }

  async function entfernenKlick(): Promise<void> {
    if (uebung === null) return;
    const ok = await confirm({
      titel: 'Übungsbestellung entfernen?',
      text: `${uebung.bestellnr} wird wirklich gelöscht — samt Positionen, Sendungen und Verlauf.`,
      bestaetigenLabel: 'Entfernen',
      destruktiv: true
    });
    if (!ok) return;
    try {
      await entfernen.mutateAsync();
      toast('success', 'Übungsbestellung entfernt.');
    } catch {
      toast('error', 'Die Übungsbestellung ließ sich nicht entfernen.');
    }
  }

  function hingehen(): void {
    if (uebung === null) return;
    // Erst das Panel schließen, dann navigieren — sonst liegt der Dialog
    // über der Seite, auf die er gerade geschickt hat.
    onClose();
    void navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr: uebung.bestellnr } });
  }

  return (
    <div className="mt-5 border-t border-line pt-4">
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-faint">
        <FlaskConical className="h-4 w-4" />
        Ausprobieren
      </h3>

      <p className="mt-2 text-sm text-ink-soft">
        Eine Übungsbestellung ist zum Anfassen da: Sie steht <strong>nicht im Brett</strong>,
        trägt eine eigene Nummer (Ü-…) und lässt sich jederzeit wieder entfernen. Nur du
        siehst sie.
      </p>
      <p className="mt-2 text-sm text-ink-soft">
        Prüfen lässt sich damit alles <em>in</em> der Bestellung — Positionen, Sendungen,
        Checkliste, Verlauf, Termine. Nicht prüfen lässt sich, was nur am Brett erscheint:
        Zustands-Chip in der Zeile, Zähler, Filter, Überfälligkeit. Dass sie dort fehlt, ist
        kein Fehler.
      </p>

      {isLoading && (
        <p className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
          <Spinner /> Wird nachgesehen …
        </p>
      )}

      {!isLoading && uebung !== null && (
        <div className="mt-3 rounded-md border border-line bg-cream-warm p-3">
          <p className="text-sm text-ink">
            Deine Übungsbestellung:{' '}
            <span className="font-serif tabular-nums">{uebung.bestellnr}</span>
          </p>
          {/* Der Weg zu ihr ist keine Bequemlichkeit, sondern notwendig:
              Über das Brett ist sie nicht zu finden (sie ist dort
              ausgeblendet). Ohne diesen Knopf legt jemand eine an und
              sieht sie nie wieder. */}
          <button
            type="button"
            onClick={hingehen}
            className="mt-2 inline-flex min-h-[44px] items-center gap-2 text-sm text-akzent hover:text-akzent-stark"
          >
            <ArrowRight className="h-4 w-4" />
            Übungsbestellung öffnen
          </button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void anlegenKlick()}
          disabled={laeuft}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line bg-cream px-3 py-2 text-sm text-ink hover:border-akzent hover:text-akzent disabled:opacity-60"
        >
          {anlegen.isPending ? <Spinner /> : <Plus className="h-4 w-4" />}
          {uebung === null ? 'Übungsbestellung anlegen' : 'Neue anlegen'}
        </button>
        {uebung !== null && (
          <button
            type="button"
            onClick={() => void entfernenKlick()}
            disabled={laeuft}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line bg-cream px-3 py-2 text-sm text-ink-soft hover:border-coral hover:text-coral disabled:opacity-60"
          >
            {entfernen.isPending ? <Spinner /> : <Trash2 className="h-4 w-4" />}
            Entfernen
          </button>
        )}
      </div>

      {/* DER SATZ, DER NICHT FEHLEN DARF (A53.2). Er steht immer da, auch
          wenn gerade keine Übung existiert — dann ist er die Ankündigung,
          und wer sie einmal gelesen hat, weiß es beim nächsten Mal. */}
      <p className="mt-2 text-xs text-ink-faint">
        Es gibt immer nur eine: Eine neue ersetzt die vorhandene.
      </p>

      {dialog}
    </div>
  );
}
